import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { kontoFormOk, kontoSuche, personenkonto } from './logik';
import type { KontoListe } from './types';

// Gegenkonto: Nummer tippen (oder ein Wort aus dem Namen), darunter die passenden Konten mit Namen,
// Vorschlagskonten oben. ↑↓ wählt, Enter übernimmt und springt weiter (onEnter), Esc schließt die Liste.
// Unbekannte vierstellige Konten sind erlaubt; dann steht nur ein Hinweis darunter.

const RAND = 8;
const LISTE_HOCH = 260;

// Lage der Liste am Feld (position: fixed, Maße des Fensters): Im Detail rechts und in der Tabelle, die beide selbst
// scrollen, würde eine Liste darin am Rand abgeschnitten. Unter dem Feld, außer dort ist kaum Platz und über dem Feld
// mehr (bis zur Kopfleiste); nie breiter als das Fenster.
function listenLage(feld: DOMRect, oben0: number): CSSProperties {
  const breite = Math.min(Math.max(feld.width, 300), window.innerWidth - 2 * RAND);
  const left = Math.max(RAND, Math.min(feld.left, window.innerWidth - breite - RAND));
  const unten = window.innerHeight - feld.bottom - RAND;
  const oben = feld.top - oben0 - RAND;
  if (unten < 160 && oben > unten) {
    return { left, width: breite, bottom: window.innerHeight - feld.top + 2, maxHeight: Math.min(LISTE_HOCH, oben) };
  }
  return { left, width: breite, top: feld.bottom + 2, maxHeight: Math.min(LISTE_HOCH, Math.max(unten, 120)) };
}

export function KontoFeld({
  value,
  onChange,
  liste,
  nameFallback,
  label,
  feld,
  disabled,
  onEnter,
}: {
  value: string;
  onChange(v: string): void;
  liste: KontoListe | undefined;
  // Name vom Server, solange die Kontenliste fehlt (z. B. Debitor)
  nameFallback: string | null;
  label: string;
  feld: string;
  disabled?: boolean;
  onEnter(): void;
}) {
  const id = useId();
  const [offen, setOffen] = useState(false);
  const [aktiv, setAktiv] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);
  const inRef = useRef<HTMLInputElement>(null);
  const [lage, setLage] = useState<CSSProperties>();
  const treffer = useMemo(() => (liste ? kontoSuche(liste.konten, value) : []), [liste, value]);
  const zeigen = offen && treffer.length > 0;
  const eintrag = liste?.konten.find((k) => k.nr === value.trim());
  const name = eintrag?.name ?? (value.trim() && nameFallback ? nameFallback : null);

  // Lage vor dem Zeichnen und neu bei jedem Scrollen (auch im Detail) und bei neuer Fenstergröße
  useLayoutEffect(() => {
    if (!zeigen) return;
    const setzen = () => {
      const el = inRef.current;
      if (!el) return;
      const kopf = el.closest('.kz')?.querySelector('.top')?.getBoundingClientRect().bottom ?? 0;
      setLage(listenLage(el.getBoundingClientRect(), Math.max(0, kopf)));
    };
    setzen();
    window.addEventListener('scroll', setzen, true);
    window.addEventListener('resize', setzen);
    return () => {
      window.removeEventListener('scroll', setzen, true);
      window.removeEventListener('resize', setzen);
    };
  }, [zeigen, value]);

  useEffect(() => {
    if (!zeigen || aktiv < 0) return;
    listRef.current?.querySelector(`[data-i="${aktiv}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [zeigen, aktiv]);

  function waehlen(nr: string, weiter: boolean) {
    onChange(nr);
    setOffen(false);
    setAktiv(-1);
    if (weiter) onEnter();
  }

  function taste(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape' && zeigen) {
      e.preventDefault();
      e.stopPropagation();
      setOffen(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (!treffer.length) return;
      setOffen(true);
      const n = treffer.length;
      setAktiv((a) => (e.key === 'ArrowDown' ? (a + 1) % n : a <= 0 ? n - 1 : a - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const t = zeigen && aktiv >= 0 ? treffer[aktiv] : undefined;
      if (t) waehlen(t.nr, true);
      else {
        setOffen(false);
        onEnter();
      }
    }
  }

  const person = !eintrag && personenkonto(value.trim());
  const fehlt = !!liste && kontoFormOk(value.trim()) && !eintrag && !nameFallback && !person;
  const ungueltig = value.trim() !== '' && !/^\d+$/.test(value.trim()) && !zeigen;
  return (
    <div className="kf">
      <label htmlFor={`${id}-i`}>{label}</label>
      <span className="kf-in">
      <input
        ref={inRef}
        id={`${id}-i`}
        className="num"
        value={value}
        disabled={disabled}
        data-feld={feld}
        data-enter=""
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={zeigen}
        aria-controls={`${id}-l`}
        aria-activedescendant={zeigen && aktiv >= 0 ? `${id}-${aktiv}` : undefined}
        aria-describedby={`${id}-n`}
        aria-invalid={ungueltig || undefined}
        autoComplete="off"
        spellCheck={false}
        enterKeyHint="next"
        maxLength={40}
        onChange={(e) => {
          onChange(e.target.value);
          setOffen(true);
          // getippt: erster Treffer ist markiert, damit Enter ihn nimmt
          setAktiv(e.target.value.trim() ? 0 : -1);
        }}
        onFocus={(e) => {
          e.target.select();
          setOffen(true);
          setAktiv(-1);
        }}
        onBlur={() => setOffen(false)}
        onKeyDown={taste}
      />
      {zeigen && (
        <ul className="kf-list" style={lage} role="listbox" id={`${id}-l`} ref={listRef} aria-label={`${label}: Konten`}>
          {treffer.map((k, i) => (
            <li
              key={k.nr}
              id={`${id}-${i}`}
              data-i={i}
              role="option"
              aria-selected={i === aktiv}
              className={i === aktiv ? 'on' : undefined}
              // vor dem Verlassen des Feldes wählen (sonst schließt die Liste zuerst)
              onMouseDown={(e) => {
                e.preventDefault();
                waehlen(k.nr, false);
              }}
            >
              <b className="num">{k.nr}</b>
              <span>{k.name}</span>
              {k.automatik && <small>rechnet automatisch</small>}
            </li>
          ))}
        </ul>
      )}
      </span>
      <span id={`${id}-n`} className={`kf-name${fehlt || ungueltig ? ' warn' : ''}`}>
        {name ??
          (person
            ? 'Personenkonto (Debitor)'
            : fehlt
              ? 'Nicht in der Kontenliste. Bitte prüfen.'
              : ungueltig
                ? 'Bitte eine Kontonummer wählen.'
                : '\u00a0')}
      </span>
    </div>
  );
}
